/** A local white brush in visual-field degrees. No DOM, media, storage or sampler. */
export const PAINT_RADIUS_DEG = 12;
export const BRUSH_RADIUS_DEG = 0.5;
export interface FieldPoint { x: number; y: number }
export interface PaintPixels { width: number; height: number; data: Uint8ClampedArray }

export const PAINT_SPOTS = [
  { label: 'Upper left', x: -4, y: 4 },
  { label: 'Upper right', x: 4, y: 4 },
  { label: 'Lower left', x: -4, y: -4 },
  { label: 'Lower right', x: 4, y: -4 },
] as const;

export function clearPaintPixels(image: PaintPixels): void {
  image.data.fill(0);
  for (let i = 3; i < image.data.length; i += 4) image.data[i] = 255;
}

/** Rasterize the swept disc, not isolated pointer samples: fast drags leave no gaps.
 * Pixel locations use the sampler's square endpoints; x right, y up. The circular
 * aperture clips the ink, including a stroke whose pointer is captured outside it. */
export function paintSegment(image: PaintPixels, from: FieldPoint, to: FieldPoint = from): boolean {
  if (![from.x, from.y, to.x, to.y].every(Number.isFinite)) return false;
  const { width, height, data } = image;
  const r = BRUSH_RADIUS_DEG, field = PAINT_RADIUS_DEG;
  const col = (x: number) => (0.5 + x / (2 * field)) * (width - 1);
  const row = (y: number) => (0.5 - y / (2 * field)) * (height - 1);
  const x0 = Math.max(0, Math.floor(col(Math.min(from.x, to.x) - r)));
  const x1 = Math.min(width - 1, Math.ceil(col(Math.max(from.x, to.x) + r)));
  const y0 = Math.max(0, Math.floor(row(Math.max(from.y, to.y) + r)));
  const y1 = Math.min(height - 1, Math.ceil(row(Math.min(from.y, to.y) - r)));
  const dx = to.x - from.x, dy = to.y - from.y, length2 = dx * dx + dy * dy;
  let changed = false;
  for (let j = y0; j <= y1; j++) for (let i = x0; i <= x1; i++) {
    const x = (i / (width - 1) - 0.5) * 2 * field;
    const y = (0.5 - j / (height - 1)) * 2 * field;
    if (x * x + y * y > field * field) continue;
    const t = length2 === 0 ? 0 : Math.max(0, Math.min(1, ((x - from.x) * dx + (y - from.y) * dy) / length2));
    if ((x - from.x - t * dx) ** 2 + (y - from.y - t * dy) ** 2 > r * r) continue;
    const k = 4 * (j * width + i);
    if (data[k] === 255) continue;
    data[k] = data[k + 1] = data[k + 2] = 255;
    changed = true;
  }
  return changed;
}
